<template>
  <v-card>
    <v-card-title>{{$lang.global.selectTime}}</v-card-title>
    <v-card-text>
      <v-form>
        <v-layout column>
          <v-layout row wrap>
            <v-flex xs12 sm6 class="px-2">
              <label for="hours">{{$lang.labels.hour}}</label>
              <v-select
                :items="formHours"
                v-model="hour"
                :label="$lang.labels.selectHours"
                single-line
              ></v-select>
            </v-flex>
            <v-flex xs12 sm6 class="px-2">
              <label for="minutes">{{$lang.labels.minute}}</label>
              <v-select
                :items="new Array(60).fill('x').map((val, index) => (index < 10 ? `0${index}` : `${index}`))"
                v-if="maxMinutes"
                v-model="minutes"
                :label="$lang.labels.selectMinute"
                single-line
              ></v-select>
            </v-flex>
          </v-layout>
        </v-layout>
      </v-form>
    </v-card-text>
    <v-alert color="error" icon="warning" v-if="err" :value="err !== ''">
      {{err}}
    </v-alert>
  <v-card-actions>
    <v-layout row justify-space-between>
      <v-btn
        outline
        color="primary"
        @click.stop="$emit('close')">{{$lang.actions.cancel}}</v-btn>
      <v-btn
        @click="submit"
        color="primary"
        :disabled="!valid">
        {{$lang.actions.submit}}
      </v-btn>
    </v-layout>
    </v-card-actions>
  </v-card>
</template>

<script>
  import { mapGetters } from 'vuex';
  import moment from 'moment';

  export default {
    props: ['time'],
    data() {
      return {
        hour: null,
        minutes: null,
        maxHour: 24,
        overHours: 0,
        maxMinutes: 60,
        minHour: 0,
        minMinutes: 0,
        currentMinMinutes: 0,
        err: '',
        warning: '',
        valid: false,
        canGoIntoNextDay: false,
        formHours: [],
      };
    },
    computed: mapGetters({
      company: 'company',
      workingHours: 'currentWorkingHours',
      tomorrowsWorkingHours: 'tomorrowsWorkingHours',
    }),
    watch: {
      hour() {
        this.checkIfValidTime();
      },
      minutes() {
        this.checkIfValidTime();
      },
    },
    methods: {
      selectHour(hour) {
        this.warning = '';
        this.currentMinMinutes = parseInt(hour.text, 10) === this.minHour ? this.minMinutes : 0;
        this.minutes = this.currentMinMinutes;
        this.checkIfValidTime();
      },
      doubleDigits(time) {
        return `${time}`.length === 1 ? `0${time}` : time;
      },
      isInWorkingHours(picked) {
        let inside = false;
        this.workingHours.forEach((entry) => {
          const start = moment(entry.startDate);
          const end = moment(entry.endDate);
          if (picked.isBetween(start, end) || picked.toISOString() === entry.startDate) {
            inside = true;
          }
          if (
            !inside &&
            (start.isBefore(picked) && entry.end === 2359 && this.canGoIntoNextDay)
          ) {
            inside = true;
          }
        });
        return inside;
      },
      findNextWorkingTime(picked) {
        let foundHours;
        this.workingHours.forEach((range) => {
          if (!foundHours &&
            (moment(range.startDate).isAfter(picked) || range.startDate === picked.toISOString())
          ) {
            foundHours = moment(range.startDate);
          }
        });
        return foundHours;
      },
      checkIfValidTime(init) {
        if (this.minutes === null) return;
        this.err = '';
        this.valid = false;
        const chosen = moment(this.hour);
        chosen.set('minutes', this.minutes);
        if (!this.isInWorkingHours(chosen)) {
          this.err = init ? this.$lang.errors.bizzHours : this.$lang.errors.bizzHours2;
          return;
        }
        const durationFrom = this.company.deliveryDurationFrom;
        if (chosen.isBefore(moment().add(durationFrom - 1, 'minutes'))) {
          this.err = `${this.$lang.errors.chosenTime} ${durationFrom} ${this.$lang.errors.minutes}`;
          return;
        }
        this.valid = true;
      },
      submit() {
        if (!this.valid) return;
        const chosen = moment(this.hour);
        chosen.set('minutes', this.minutes);
        this.$emit('submit', chosen);
        this.$emit('close');
      },
      generateFormHours(min, max, overHours) {
        let isToday = true;
        const maximum = max === 0 ? 24 : max;
        const now = moment().format('YYYY-MM-DD');
        const tomorrow = moment().add(1, 'day').format('YYYY-MM-DD');
        const hours = new Array((maximum - min) + overHours).fill('x').map((val, index) => {
          const hour = min + index;
          if (hour === 24) isToday = false;
          return {
            text: this.doubleDigits(hour === 24 ? 0 : hour),
            value: moment(`${isToday ? now : tomorrow} ${hour === 24 ? 0 : hour}:00`).toISOString(),
          };
        });
        hours.sort((a, b) => {
          if (a.startDate < b.startDate) return -1;
          return a.startDate > b.startDate ? 1 : 0;
        });
        return hours;
      },
    },
    created() {
      if (this.workingHours.length === 0) {
        this.err = this.$lang.errors.restaurantClosedToday;
        return;
      }
      const now = new Date();
      const day1End = moment(this.workingHours[this.workingHours.length - 1].endDate);
      if (this.tomorrowsWorkingHours[0]) {
        const day2Start = moment(this.tomorrowsWorkingHours[0].startDate).add(1, 'day');
        const duration = moment.duration(day2Start.diff(day1End)).asMinutes();
        this.canGoIntoNextDay = duration === 1;
      }
      this.maxHour = parseInt(this.workingHours[this.workingHours.length - 1].end.split(':')[0], 10);
      if (parseInt(this.workingHours[this.workingHours.length - 1].end.split(':')[1], 10) > 0) {
        this.overHours = 1;
      }
      const durationFrom = this.company.deliveryDurationFrom;
      const currentTime = moment().add(durationFrom, 'minutes');
      if (!this.isInWorkingHours(currentTime)) {
        const foundHours = this.findNextWorkingTime(currentTime);
        this.minHour = parseInt(foundHours.format('HH'), 10);
      } else {
        this.minHour = now.getHours();
      }
      this.formHours = this.generateFormHours(this.minHour, this.maxHour, this.overHours);
      let time = this.time;
      if (time === -1) {
        time = moment().add(durationFrom, 'minutes');
      }
      if (!this.isInWorkingHours(time)) {
        time = this.findNextWorkingTime(time);
        if (!time) return;
      }
      this.hour = moment(`${time.format('YYYY-MM-DD HH')}:00`).toISOString();
      this.minMinutes = time.format('HH') === this.minHour ? now.getMinutes() : 0;
      this.minutes = time.format('mm');
      this.currentMinMinutes = this.minMinutes;
    },
  };

</script>



// WEBPACK FOOTER //
// src/components/webshop/modals/Time.vue